import { Box, PopoverOrigin } from '@mui/material';
import { useCallback, useState, useMemo } from 'react';
import {
  AdvancedFilterPopover,
  AdvancedFilterPopoverTexts,
  defaultTexts as defaultPopoverTexts,
} from './AdvancedFilterPopover';
import { Button, IconButton } from '@sweep-io/sweep-design';
import { FilterLines } from '@sweep-io/sweep-design/dist/icons';
import { FilterButton } from '../../FilterButton';
import { SweepTypographyVariants } from '@sweep-io/sweep-design/dist/components/Typography/types';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';

interface AdvancedFilterTexts extends AdvancedFilterPopoverTexts {
  allSelected: string;
}

const defaultTexts: AdvancedFilterTexts = {
  allSelected: 'All',
  ...defaultPopoverTexts,
};

export type AdvancedFilterItemValue<DataT = any> = {
  value: string;
  label: string;
  data?: DataT;
  isSectionItem?: boolean; // Non selectable item used as a section
};

export interface AdvancedFilterItem<DataT = any> extends AdvancedFilterItemValue<DataT> {
  labelDecoration?: React.ReactNode;
}

export interface AdvancedFilterProps<DataT = any> {
  items: AdvancedFilterItem<DataT>[];
  selectedItems: string[];
  onSelectedItemsChange: (items: string[], itemsData: AdvancedFilterItemValue<DataT>[]) => void;
  texts?: Partial<AdvancedFilterTexts>;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  disabled?: boolean;
  onlyIcon?: boolean;
  hideLabels?: boolean;
  renderSeparateClearButton?: boolean;
  filterButtonDataTestId?: string;
  enableVirtualScroll?: boolean; // if true, the popover will be rendered using Virtuoso when there's more than 50 items
  maxWidth?: string; //beyond this width, text will be truncated
  highlightChangedFilter?: boolean;
  titleVariant?: SweepTypographyVariants;
  titleColor?: string;
}

export type AdvancedFilterRef = {
  clear: () => void;
};

export function itemToItemValue<DataT = any>(
  item: AdvancedFilterItem<DataT>,
): AdvancedFilterItemValue<DataT> {
  return {
    value: item.value,
    label: item.label,
    data: item.data,
  };
}

export const AdvancedFilter = function <DataT = any>({
  items,
  selectedItems,
  onSelectedItemsChange,
  texts,
  anchorOrigin,
  transformOrigin,
  disabled,
  onlyIcon,
  hideLabels,
  filterButtonDataTestId,
  renderSeparateClearButton,
  enableVirtualScroll,
  maxWidth,
  highlightChangedFilter,
  titleVariant,
  titleColor,
}: AdvancedFilterProps<DataT>) {
  const { newAdvancedFilterDesign } = useFeatureToggle();

  const _texts: AdvancedFilterTexts = useMemo(
    () => ({
      ...defaultTexts,
      ...texts,
    }),
    [texts],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl],
  );

  const selectableItems = useMemo(() => items.filter((item) => !item.isSectionItem), [items]);

  const { filterButtonText, filterButtonTooltip, hasSelectedItems, allSelected } = useMemo(() => {
    const hasSelectedItems = selectedItems.length > 0;
    const allSelected =
      selectedItems.length === 0 || selectedItems.length === selectableItems.length;
    const title = _texts.title;

    if (newAdvancedFilterDesign) {
      let filterButtonText: string;
      let filterButtonTooltip = '';

      if (allSelected) {
        filterButtonText = title;
      } else if (selectedItems.length === 1) {
        const selectedItem = selectableItems.find((item) => item.value === selectedItems[0]);
        filterButtonText = `${title} (${selectedItem?.label || ''})`;
      } else {
        filterButtonText = `${title} (${selectedItems.length})`;
        const chosenItems = selectableItems
          .filter((item) => selectedItems.includes(item.value))
          .map((item) => item.label);
        filterButtonTooltip = chosenItems.join(', ');
      }
      return { filterButtonText, filterButtonTooltip, hasSelectedItems, allSelected };
    }

    let filterButtonText = _texts.allSelected;
    let filterButtonTooltip = '';

    if (hasSelectedItems) {
      if (allSelected) {
        filterButtonText = _texts.allSelected;
      } else {
        if (selectedItems.length > 0) {
          const firstSelectedItem = items.find((item) => item.value === selectedItems[0]);
          filterButtonText = firstSelectedItem?.label || '';

          if (selectedItems.length > 1) {
            filterButtonText += ` +${selectedItems.length - 1}`;

            const chosenItems = items
              .filter((item) => selectedItems.includes(item.value))
              ?.map((item) => item.label);
            filterButtonTooltip = chosenItems?.join(', ');
          }
        } else {
          filterButtonText = _texts.allSelected;
        }
      }
    }

    return { filterButtonText, filterButtonTooltip, hasSelectedItems, allSelected };
  }, [_texts, items, selectedItems, selectableItems, newAdvancedFilterDesign]);

  const clearAll = useCallback(() => {
    onSelectedItemsChange([], []);
  }, [onSelectedItemsChange]);

  const showClearButton = hasSelectedItems && !allSelected;
  const isHighlighted = !!(highlightChangedFilter && hasSelectedItems && !allSelected);
  const isHighlightedV2 =
    newAdvancedFilterDesign && highlightChangedFilter !== false && !allSelected;

  return (
    <Box
      className="advanced-filter"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gap={1.5}
    >
      <Box sx={{ display: 'flex', minWidth: 0 }}>
        {onlyIcon && (
          <IconButton onClick={handleClick} variant="system">
            <>
              <FilterLines />
            </>
          </IconButton>
        )}

        {!onlyIcon && (
          <FilterButton
            dataTestId={filterButtonDataTestId}
            isPressed={Boolean(anchorEl) || isHighlighted || isHighlightedV2}
            isOpen={Boolean(anchorEl)}
            handleClick={handleClick}
            text={filterButtonText}
            showClearBtn={showClearButton && !renderSeparateClearButton}
            onClearBtn={clearAll}
            tooltip={filterButtonTooltip}
            title={texts?.title ?? ''}
            disabled={disabled}
            maxWidth={maxWidth}
            titleVariant={titleVariant}
            titleColor={titleColor}
          />
        )}

        <AdvancedFilterPopover
          items={items}
          selectedItems={selectedItems}
          anchorEl={anchorEl}
          onSelectedItemsChange={onSelectedItemsChange}
          onClose={() => setAnchorEl(null)}
          texts={texts}
          transformOrigin={transformOrigin}
          anchorOrigin={anchorOrigin}
          hideLabels={hideLabels}
          enableVirtualScroll={enableVirtualScroll}
        />
      </Box>

      {showClearButton && renderSeparateClearButton && (
        <Button variant="flat-blue" size="small" startIconName="Close" onClick={() => clearAll()}>
          Clear
        </Button>
      )}
    </Box>
  );
};
